import { ApolloError, useQuery } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { mergeStyles } from '@fluentui/react';
import { Field, Input, Select, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { GET_DIVIDEND_PAYMENT_TYPES_QUERY } from '../../../../utils/statApi/CompanyFiscalYearsApi';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import CurrencyCodeDropdown from '../../../common/formFields/CurrencyCodeDropdown';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import { IDividendPayment } from '../../serviceContacts/interfaces';

const DividendPaymentTypeDropdown = (props: IFieldsetProps): JSX.Element => {
  const { value, label, errors, handleChange, valueKey, outputKey } = props;
  const { loading, data } = useQuery(GET_DIVIDEND_PAYMENT_TYPES_QUERY);
  const { dividendPaymentTypes } = !loading && data ? data : [];

  return (
    <QueryBasedDropdown
      value={value as IDropdownReference}
      label={label}
      outputKey={outputKey}
      dropdownOptionsData={dividendPaymentTypes}
      errors={errors}
      handleChange={handleChange}
      valueKey={valueKey}
      required
    />
  );
};

interface IDividendPaymentFormProps {
  mutationLoading: boolean;
  mutationError: ApolloError;
  dividendPayment: IDividendPayment;
  onSubmit: (dividendPaymentInput: IDividendPayment) => void;
  theForm: UseFormMethods<Record<string, unknown>>;
}

const DividendPaymentForm = ({
  mutationLoading,
  mutationError,
  dividendPayment,
  onSubmit,
  theForm,
}: IDividendPaymentFormProps): JSX.Element => {
  const { errors, setValue, control, handleSubmit } = theForm;

  const dividendTypeOptions: IDropdownOption[] = [
    { key: 'ANNUAL', text: 'Annual' },
    { key: 'INTERIM', text: 'Interim' },
  ];

  const dividendAmountStyles = mergeStyles({
    width: '295px',
  });

  return (
    <FluentProvider theme={webLightTheme}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LoadingErrorMessage error={mutationError} loading={mutationLoading} />
        <Controller
          name="dividendPaymentType"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <DividendPaymentTypeDropdown
              label="Dividend Payment Type"
              value={dividendPayment.dividendPaymentType}
              valueKey="dividendPaymentType"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('dividendPaymentType', newValue);
              }}
              errors={errors}
            />
          )}
          defaultValue={dividendPayment.dividendPaymentType}
          rules={{
            required: 'Please select a dividend payment type',
          }}
          required
        />
        <Controller
          name="dividendType"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field label="Dividend Type" required validationMessage={errors?.dividendType?.message}>
              <Select
                onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                  setValue('dividendType', event.target.value);
                }}
                defaultValue={dividendPayment?.dividendType || ''}
              >
                <option disabled value="">
                  Select an option
                </option>
                {dividendTypeOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          defaultValue={dividendPayment?.dividendType}
          rules={{
            required: 'Please select a dividend type',
          }}
        />
        <Stack horizontal tokens={{ childrenGap: 5 }}>
          <Controller
            name="amount"
            control={control}
            defaultValue={dividendPayment?.amount?.toString()}
            render={({ onChange, value, name }): React.ReactElement => (
              <Field label="Amount" required validationMessage={errors?.amount?.message}>
                <Input
                  name={name}
                  type="number"
                  value={value}
                  onChange={(e, inputData): void => {
                    onChange(inputData.value);
                  }}
                  className={dividendAmountStyles}
                />
              </Field>
            )}
            rules={{
              required: 'Please enter an amount for the dividend payment',
              min: {
                value: 0,
                message: 'Please enter a positive number',
              },
            }}
          />
          <StackItem>
            <Controller
              name="currencyCode"
              control={control}
              render={({ onChange, onBlur, value, name }): React.ReactElement => (
                <CurrencyCodeDropdown
                  propertyName="currencyCode"
                  handleChange={(newValue: string, valueKey: string): void => {
                    setValue('currencyCode', newValue);
                  }}
                  defaultValue={dividendPayment?.currencyCode}
                  errorMessage={errors?.currencyCode?.message}
                  required
                />
              )}
              defaultValue={dividendPayment.currencyCode}
              rules={{
                required: 'Please provide a Currency Code',
              }}
              required
            />
          </StackItem>
        </Stack>
        <Controller
          name="paymentDate"
          control={control}
          as={UtcDatePicker}
          label="Payment Date"
          allowTextInput
          textField={{
            name: 'paymentDate',
            errorMessage: errors?.paymentDate?.message,
          }}
          defaultValue={dividendPayment?.paymentDate}
          onSelectDate={(utcDateString: string): void => {
            setValue('paymentDate', utcDateString);
          }}
          rules={{
            required: 'Please enter a payment date',
          }}
          isRequired
        />
        <Controller
          name="fxRate"
          control={control}
          defaultValue={dividendPayment?.fxRate?.toString()}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="FX Rate" validationMessage={errors?.fxRate?.message}>
              <Input
                name={name}
                type="number"
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            min: { value: 0, message: 'Please enter a positive number' },
          }}
        />
        <Controller
          name="whRate"
          control={control}
          defaultValue={dividendPayment?.whRate?.toString()}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="WH Rate" validationMessage={errors?.whRate?.message}>
              <Input
                name={name}
                type="number"
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            min: { value: 0, message: 'Please enter a positive number' },
          }}
        />
        <Controller
          name="whAmount"
          control={control}
          defaultValue={dividendPayment?.whAmount?.toString()}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="WH Amount" required validationMessage={errors?.whAmount?.message}>
              <Input
                name={name}
                type="number"
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            min: { value: 0, message: 'Please enter a positive number' },
          }}
        />
      </form>
    </FluentProvider>
  );
};

export default DividendPaymentForm;
